import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import immigrationNewsService from '../services/immigrationNewsService';
import { useAuth } from '../contexts/AuthContext';

const ImmigrationNewsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const loadNewsDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const newsData = await immigrationNewsService.getNewsDetail(id);
      setNews(newsData);
    } catch (err) {
      console.error('Error loading news detail:', err);
      setError('Failed to load news article');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadRelatedNews = useCallback(async () => {
    try {
      setRelatedLoading(true);
      const related = await immigrationNewsService.getRelatedNews(id);
      setRelatedNews(related);
    } catch (err) {
      console.error('Error loading related news:', err);
    } finally {
      setRelatedLoading(false);
    }
  }, [id]);

  const loadComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const commentsData = await immigrationNewsService.getComments(id);
      setComments(commentsData);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    try {
      const result = await immigrationNewsService.toggleLike(id);
      setNews(prev => ({
        ...prev,
        is_liked: result.liked,
        likes_count: result.likes_count
      }));
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleShare = async (platform = 'direct') => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    try {
      const result = await immigrationNewsService.shareNews(id, platform);
      setNews(prev => ({
        ...prev,
        shares_count: result.shares_count
      }));
      
      // Copy URL to clipboard for direct sharing
      if (platform === 'direct') {
        await navigator.clipboard.writeText(window.location.href);
        alert('Article link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing news:', err);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!newComment.trim()) return;
    
    try {
      setSubmittingComment(true);
      const comment = await immigrationNewsService.addComment(id, newComment);
      setComments(prev => [comment, ...prev]);
      setNews(prev => ({
        ...prev,
        comments_count: prev.comments_count + 1
      }));
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!isAuthenticated) return;
    
    try {
      await immigrationNewsService.deleteComment(id, commentId);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
      setNews(prev => ({
        ...prev,
        comments_count: prev.comments_count - 1
      }));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  useEffect(() => {
    if (id) {
      loadNewsDetail();
      loadRelatedNews();
      loadComments();
    }
  }, [id, loadNewsDetail, loadRelatedNews, loadComments]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return colors[priority] || colors.medium;
  };

  const getCountryFlag = (country) => {
    const flags = {
      'USA': '🇺🇸',
      'UK': '🇬🇧',
      'Canada': '🇨🇦',
      'Australia': '🇦🇺',
      'Germany': '🇩🇪',
      'New Zealand': '🇳🇿',
      'Pakistan': '🇵🇰'
    };
    return flags[country] || '🌍';
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </PageLayout>
    );
  }

  if (error || !news) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The requested article could not be found.'}</p>
            <Link
              to="/visa"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Visa News
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center space-x-2 text-sm">
              <Link to="/" className="text-gray-500 hover:text-gray-700">Home</Link>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <Link to="/visa" className="text-gray-500 hover:text-gray-700">Visa Information</Link>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-gray-900 font-medium">News Article</span>
            </nav>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <article className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Article Header */}
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(news.priority)}`}>
                        {news.priority.toUpperCase()}
                      </span>
                      {news.country && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-medium">
                          {getCountryFlag(news.country)} {news.country}
                        </span>
                      )}
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                        {news.category}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{formatDate(news.published_date)}</p>
                      <p className="text-sm text-gray-500">{news.view_count} views</p>
                    </div>
                  </div>

                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    {news.title}
                  </h1>

                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600">Source: {news.source_name}</span>
                      {news.ai_confidence > 0.8 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                          ✓ Verified
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => window.open(news.source_url, '_blank')}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Read Original
                      </button>
                    </div>
                  </div>

                  {/* Social Interaction Buttons */}
                  <div className="flex items-center justify-between mb-8 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-6">
                      <button
                        onClick={handleLike}
                        className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                          news.is_liked 
                            ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <svg className="w-5 h-5" fill={news.is_liked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        <span className="font-medium">{news.likes_count}</span>
                      </button>

                      <button
                        onClick={() => handleShare('direct')}
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                        </svg>
                        <span className="font-medium">{news.shares_count}</span>
                      </button>
                    </div>

                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>{news.view_count} views</span>
                    </div>
                  </div>
                </div>

                {/* Article Image */}
                {news.image_url && (
                  <div className="relative">
                    <img
                      src={news.image_url}
                      alt={news.image_alt || news.title}
                      className="w-full h-64 lg:h-96 object-cover"
                    />
                    <div className="absolute bottom-4 right-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-black/50 text-white text-xs">
                        {news.image_source}
                      </span>
                    </div>
                  </div>
                )}

                {/* Article Content */}
                <div className="p-8">
                  <div className="prose prose-lg max-w-none">
                    <div className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">
                      {news.content}
                    </div>
                  </div>

                  {/* AI Tags */}
                  {news.ai_tags && news.ai_tags.length > 0 && (
                    <div className="mt-8 pt-8 border-t border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Topics</h3>
                      <div className="flex flex-wrap gap-2">
                        {news.ai_tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </article>

              {/* Comments Section */}
              <div className="bg-white rounded-2xl shadow-lg p-8 mt-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Comments ({news.comments_count})
                  </h3>
                </div>

                {/* Add Comment Form */}
                {isAuthenticated ? (
                  <form onSubmit={handleAddComment} className="mb-8">
                    <div className="flex space-x-4">
                      <div className="flex-1">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Share your thoughts on this article..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          rows={3}
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submittingComment || !newComment.trim()}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {submittingComment ? 'Posting...' : 'Post'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="mb-8 p-4 bg-blue-50 rounded-lg">
                    <p className="text-blue-800 text-sm">
                      <Link to="/login" className="font-medium hover:underline">
                        Sign in
                      </Link> to join the conversation and share your thoughts.
                    </p>
                  </div>
                )}

                {/* Comments List */}
                {commentsLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : comments.length > 0 ? (
                  <div className="space-y-6">
                    {comments.map((comment) => (
                      <div key={comment.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            {comment.user_avatar ? (
                              <img
                                src={comment.user_avatar}
                                alt={comment.user_name}
                                className="w-10 h-10 rounded-full"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{comment.user_name}</p>
                                <p className="text-xs text-gray-500">
                                  {new Date(comment.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              {isAuthenticated && (user?.email === comment.user_name || user?.is_staff) && (
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            <p className="mt-2 text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-gray-500">No comments yet. Be the first to share your thoughts!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Related News */}
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Related Articles</h3>
                
                {relatedLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : relatedNews.length > 0 ? (
                  <div className="space-y-4">
                    {relatedNews.map((related) => (
                      <Link
                        key={related.id}
                        to={`/immigration-news/${related.id}`}
                        className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-start space-x-3">
                          {related.image_url && (
                            <img
                              src={related.image_url}
                              alt={related.image_alt || related.title}
                              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">
                              {related.title}
                            </h4>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              {related.country && (
                                <span>{getCountryFlag(related.country)} {related.country}</span>
                              )}
                              <span>{related.time_ago}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No related articles found.</p>
                )}
              </div>

              {/* Back to News */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <Link
                  to="/visa"
                  className="inline-flex items-center justify-center w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to All News
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ImmigrationNewsDetailPage;
